import React from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView, Linking, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as StoreReview from 'expo-store-review';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import ThemedText from '../components/common/ThemedText';
import { Alert } from '../utils/toast.native';

const SettingsScreen: React.FC = () => {
  const navigation = useNavigation();
  const { logout, user } = useAuth();
  const { theme } = useTheme();
  const isAdmin = user?.role === 'admin';

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Logout',
          style: 'destructive',
          onPress: async () => {
            try {
              await logout();
            } catch (error) {
              Alert.alert('Error', 'Failed to logout. Please try again.');
            }
          },
        },
      ]
    );
  };

  const handlePrivacyPolicy = () => {
    (navigation as any).navigate('PrivacyPolicy');
  };

  const handleTermsOfUse = () => {
    (navigation as any).navigate('TermsOfUse');
  };

  const handleContactUs = () => {
    const email = 'posteditorlnf@gmail.com';
    const subject = 'Support Request';
    const url = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
    
    Linking.openURL(url).catch(() => {
      Alert.alert(
        'Contact Us',
        `Email: ${email}\n\nYou can reach us at the above email address for any queries or support.`,
        [{ text: 'OK' }]
      );
    });
  };

  const handleRateApp = async () => {
    try {
      console.log('📱 Rate App: Opening store page...');
      
      // Directly open the store page
      const storeUrl = Platform.select({
        ios: 'https://apps.apple.com/in/app/lnb-leads-n-business/id6758208642',
        android: 'https://play.google.com/store/apps/details?id=com.land.ledgernetworkbusiness',
      });
      
      console.log('📱 Rate App: Store URL =', storeUrl);
      
      if (storeUrl) {
        const canOpen = await Linking.canOpenURL(storeUrl);
        console.log('📱 Rate App: Can open URL =', canOpen);
        
        if (canOpen) {
          await Linking.openURL(storeUrl);
          console.log('📱 Rate App: Store opened successfully');
        } else {
          throw new Error('Cannot open store URL');
        }
      } else {
        throw new Error('No store URL available');
      }
    } catch (error) {
      console.error('📱 Rate App: Error opening store:', error);
      Alert.alert(
        'Rate Our App',
        'Unable to open the store. Please search for "LNB" in your app store to rate us.',
        [{ text: 'OK' }]
      );
    }
  };

  const handleAbout = () => {
    Alert.alert(
      'About LNB',
      'Version: 1.0.0\n\nLNB is a professional networking platform designed to help businesses and professionals connect, collaborate, and grow together.\n\n© 2026 LNM Global Pvt Ltd\nAll rights reserved.',
      [{ text: 'OK' }]
    );
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      'Delete Account',
      'Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted.',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            Alert.alert(
              'Confirm Deletion',
              'Please type "DELETE" to confirm account deletion.',
              [
                {
                  text: 'Cancel',
                  style: 'cancel',
                },
                {
                  text: 'Confirm',
                  style: 'destructive',
                  onPress: async () => {
                    try {
                      // TODO: Implement actual account deletion
                      await logout();
                      Alert.alert('Account Deleted', 'Your account has been permanently deleted.');
                    } catch (error) {
                      Alert.alert('Error', 'Failed to delete account. Please try again.');
                    }
                  },
                },
              ]
            );
          },
        },
      ]
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={Platform.OS === 'web' ? [] : ['top', 'left', 'right']}>
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Settings</ThemedText>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        {/* Payments Section */}
        <View style={[styles.section, { backgroundColor: theme.backgroundSecondary }]}>
          <ThemedText style={[styles.sectionTitle, { color: theme.textSecondary }]}>Payments</ThemedText>

          <TouchableOpacity
            style={[styles.menuItem, { borderBottomColor: theme.borderLight }]}
            onPress={() => (navigation as any).navigate('MyPayments')}
          >
            <View style={styles.menuItemLeft}>
              <Ionicons name="receipt-outline" size={24} color={theme.primary} />
              <ThemedText style={[styles.menuItemText, { color: theme.text }]}>My Payments</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>

          {isAdmin && (
            <TouchableOpacity
              style={[styles.menuItem, { borderBottomColor: theme.borderLight }]}
              onPress={() => (navigation as any).navigate('AdminPayments')}
            >
              <View style={styles.menuItemLeft}>
                <Ionicons name="card-outline" size={24} color={theme.primary} />
                <ThemedText style={[styles.menuItemText, { color: theme.text }]}>All Circle Payments</ThemedText>
              </View>
              <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
            </TouchableOpacity>
          )}
        </View>

        {/* Legal Section */}
        <View style={[styles.section, { backgroundColor: theme.backgroundSecondary }]}>
          <ThemedText style={[styles.sectionTitle, { color: theme.textSecondary }]}>Legal</ThemedText>
          
          <TouchableOpacity style={[styles.menuItem, { borderBottomColor: theme.borderLight }]} onPress={handlePrivacyPolicy}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="shield-checkmark-outline" size={24} color={theme.textSecondary} />
              <ThemedText style={[styles.menuItemText, { color: theme.text }]}>Privacy Policy</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity style={[styles.menuItem, { borderBottomColor: theme.borderLight }]} onPress={handleTermsOfUse}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="document-text-outline" size={24} color={theme.textSecondary} />
              <ThemedText style={[styles.menuItemText, { color: theme.text }]}>Terms of Use</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>
        </View>

        {/* Support Section */}
        <View style={[styles.section, { backgroundColor: theme.backgroundSecondary }]}>
          <ThemedText style={[styles.sectionTitle, { color: theme.textSecondary }]}>Support</ThemedText>
          
          <TouchableOpacity style={[styles.menuItem, { borderBottomColor: theme.borderLight }]} onPress={handleContactUs}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="mail-outline" size={24} color={theme.textSecondary} />
              <ThemedText style={[styles.menuItemText, { color: theme.text }]}>Contact Us</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity style={[styles.menuItem, { borderBottomColor: theme.borderLight }]} onPress={handleRateApp}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="star-outline" size={24} color={theme.accent} />
              <ThemedText style={[styles.menuItemText, { color: theme.text }]}>Rate Our App</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>
        </View>

        {/* About Section */}
        <View style={[styles.section, { backgroundColor: theme.backgroundSecondary }]}>
          <ThemedText style={[styles.sectionTitle, { color: theme.textSecondary }]}>About</ThemedText>
          
          <TouchableOpacity style={[styles.menuItem, { borderBottomColor: theme.borderLight }]} onPress={handleAbout}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="information-circle-outline" size={24} color={theme.textSecondary} />
              <ThemedText style={[styles.menuItemText, { color: theme.text }]}>About LNB</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>
        </View>

        {/* App Store Info Section */}
        <View style={[styles.section, { backgroundColor: theme.backgroundSecondary }]}>
          <ThemedText style={[styles.sectionTitle, { color: theme.textSecondary }]}>Store Information</ThemedText>
          
          <View style={[styles.infoCard, { borderBottomColor: theme.borderLight }]}>
            <Ionicons name="mail" size={20} color={theme.accent} />
            <View style={styles.infoContent}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Support Email</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>posteditorlnf@gmail.com</ThemedText>
            </View>
          </View>

          <View style={[styles.infoCard, { borderBottomColor: theme.borderLight }]}>
            <Ionicons name="globe" size={20} color={theme.accent} />
            <View style={styles.infoContent}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Website</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>ln.ink</ThemedText>
            </View>
          </View>
        </View>

        {/* Account Actions - Moved to bottom */}
        <View style={[styles.section, { backgroundColor: theme.backgroundSecondary }]}>
          <ThemedText style={[styles.sectionTitle, { color: theme.textSecondary }]}>Account Actions</ThemedText>
          
          <TouchableOpacity style={[styles.menuItem, { borderBottomColor: theme.borderLight }]} onPress={handleLogout}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="log-out-outline" size={24} color={theme.error} />
              <ThemedText style={[styles.menuItemText, styles.logoutText, { color: theme.error }]}>Logout</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity style={[styles.menuItem, { borderBottomColor: theme.borderLight }]} onPress={handleDeleteAccount}>
            <View style={styles.menuItemLeft}>
              <Ionicons name="trash-outline" size={24} color={theme.error} />
              <ThemedText style={[styles.menuItemText, styles.deleteText, { color: theme.error }]}>Delete Account</ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color="#999999" />
          </TouchableOpacity>
        </View>

        {/* Copyright */}
        <View style={[styles.copyrightSection, { backgroundColor: theme.background }]}>
          <ThemedText style={[styles.copyrightText, { color: theme.textSecondary }]}>
            © 2026 LNM Global Pvt Ltd
          </ThemedText>
          <ThemedText style={[styles.copyrightText, { color: theme.textSecondary }]}>
            All rights reserved.
          </ThemedText>
          <ThemedText style={[styles.versionText, { color: theme.textTertiary }]}>
            Version 1.0.0
          </ThemedText>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    ...(Platform.OS === 'web' ? { position: 'sticky' as any, top: 0, zIndex: 10 } : {}),
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  headerRight: {
    width: 32,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
  },
  section: {
    paddingVertical: 8,
    marginTop: 16,
    marginHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  themeTextContainer: {
    marginLeft: 16,
    flex: 1,
  },
  themeSubtext: {
    fontSize: 12,
    marginTop: 2,
  },
  menuItemText: {
    fontSize: 16,
    marginLeft: 16,
  },
  logoutText: {
    fontWeight: '600',
  },
  deleteText: {
    fontWeight: '600',
  },
  infoCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  infoContent: {
    marginLeft: 16,
    flex: 1,
  },
  infoLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '500',
  },
  bottomPadding: {
    height: 100,
  },
  copyrightSection: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginTop: 16,
  },
  copyrightText: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 4,
  },
  versionText: {
    fontSize: 11,
    textAlign: 'center',
    marginTop: 8,
  },
});

export default SettingsScreen;
